/**
 * GENERATED by scripts/derive-process-material-config.mjs — DO NOT HAND-EDIT.
 * Re-run that script (after refreshing ProcessMaterials.csv) to regenerate.
 *
 * Customer-visible process/material/finish/color/certificate/inspection
 * reference data (UID-93), derived from the raw ERP export by replicating
 * the real Fictiv visibility rules (see that script's header comment for the
 * exact source files) rather than inventing demo content. Certificates are
 * gated per-process (never offered for urethane_casting, per
 * CERTIFICATION_ALLOWED_PROCESS_TYPES); a material only appears under a
 * process at all if it is process_active + material_active +
 * platform_material_available_to_platform (the customer-facing default).
 *
 * ADR-0117 D2 — MOVED here from apps/web/src/verticals/parts/ so BOTH apps can
 * reach it: apps/api validates `RfqLineSpecification` cascades (D4) and prices
 * them (D8) against this data, and apps/api must never import from apps/web.
 * The old web path is now a re-export shim. When the documented Manufacturing
 * Catalog service (RD-29 B #15, OQ-73) ships, this module becomes an adapter
 * behind a `ManufacturingCatalogPort` — see ADR-0117 D3.
 */
import type { PartProcess } from './part-process'

export interface ColorOption {
  readonly id: string
  readonly name: string
}

export interface FinishOption {
  readonly id: string
  readonly name: string
  readonly colors?: readonly ColorOption[]
}

export interface ProcessMaterialOption {
  readonly id: string
  readonly name: string
  /** ERP `quote_material_category_name` (e.g. "Aluminum", "Stainless steel",
   *  "Multi Jet Fusion (MJF)"); null when the export carries none. The
   *  configuration-pricing rate card keys its material base rate on this
   *  (ADR-0117 D8) so no second hand-typed material→category map is needed. */
  readonly category: string | null
  readonly finishes: readonly FinishOption[]
  /** False only for urethane_casting — certificates are never offered for that process. */
  readonly certificatesAllowed: boolean
  readonly certificates: readonly string[]
  readonly inspections: readonly string[]
}

export interface ProcessEntry {
  readonly id: PartProcess
  readonly label: string
}

/** processGroupingUtils.ts's INSTANT_MANUAL_PROCESSES_ORDER / MANUAL_QUOTE_PROCESSES_ORDER. */
export const PROCESS_GROUPS: {
  readonly instantManual: readonly ProcessEntry[]
  readonly manualQuote: readonly ProcessEntry[]
} = {
  instantManual: [
    {
      id: 'cnc_machining',
      label: 'CNC machining',
    },
    {
      id: '3d_printing',
      label: '3D printing',
    },
    {
      id: 'urethane_casting',
      label: 'Urethane casting',
    },
    {
      id: 'sheet_metal_fabrication',
      label: 'Sheet metal fabrication',
    },
  ],
  manualQuote: [
    {
      id: 'injection_molding',
      label: 'Injection molding',
    },
    {
      id: 'compression_molding',
      label: 'Compression molding',
    },
    {
      id: 'die_casting',
      label: 'Die casting',
    },
    {
      id: 'tube_bending',
      label: 'Tube bending',
    },
  ],
}

export const MATERIALS_BY_PROCESS: Readonly<Record<PartProcess, readonly ProcessMaterialOption[]>> =
  {
    cnc_machining: [
      {
        id: 'cnc-copper-101',
        name: '101 Copper',
        category: 'Copper, Brass, Bronze',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel1045',
        name: '1045 Carbon Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-copper',
        name: '110 Copper',
        category: 'Copper, Brass, Bronze',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel1215',
        name: '1215 Carbon Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel12l14',
        name: '12L14 Carbon Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss155ph',
        name: '15-5 PH Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss174ph-ann',
        name: '17-4 PH Stainless Steel (Annealed)',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss174ph-h1150',
        name: '17-4 PH Stainless Steel (H1150 Temper)',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss174ph-h900',
        name: '17-4 PH Stainless Steel (H900 Temper)',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'passivation',
            name: 'Passivation',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-al2024',
        name: '2024 Aluminum (T3 / T351 / T4)',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-brass260',
        name: '260 Brass',
        category: 'Copper, Brass, Bronze',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss-303',
        name: '303 Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss-304l',
        name: '304/304L Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss-316l',
        name: '316/316L Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-brass',
        name: '360 Brass',
        category: 'Copper, Brass, Bronze',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss-410',
        name: '410 Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel4130',
        name: '4130 Alloy Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel4140',
        name: '4140 Alloy Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss-416',
        name: '416 Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss-420',
        name: '420 Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel4340',
        name: '4340 Alloy Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss-440c',
        name: '440C Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'electropolishing',
            name: 'Electropolishing',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-al5052',
        name: '5052 Aluminum (H32)',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-bronze-544',
        name: '544 Bearing Bronze',
        category: 'Copper, Brass, Bronze',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-aluminum',
        name: '6061 Aluminum (T6 / T651 / T6511)',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-al6063',
        name: '6063 Aluminum (T5 / T52)',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-al7050',
        name: '7050 Aluminum (T7451)',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-al7075',
        name: '7075 Aluminum (T6 / T651 / T6511 / T7351)',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel8620',
        name: '8620 Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-bronze-932',
        name: '932 Bearing Bronze',
        category: 'Copper, Brass, Bronze',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-bronze-954',
        name: '954 Bearing Bronze',
        category: 'Copper, Brass, Bronze',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-custom',
        name: '954 Bronze',
        category: 'Copper, Brass, Bronze',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steela2',
        name: 'A2 Tool Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ss-a286',
        name: 'A286 Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'hardening',
            name: 'Hardening',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'tempering',
            name: 'Tempering',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steela36',
        name: 'A36 Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steela514',
        name: 'A514 Alloy Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-abs',
        name: 'ABS',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-acrylic',
        name: 'Acrylic',
        category: 'Plastic',
        finishes: [
          {
            id: 'hand-polishing',
            name: 'Hand Polishing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'fluorescent-green',
                name: 'Fluorescent Green',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'matte-black',
                name: 'Matte Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-magnesium-az31b',
        name: 'AZ31B Magnesium',
        category: 'Exotic metal',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-magnesium-az61b',
        name: 'AZ61B Magnesium',
        category: 'Exotic metal',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-magnesium-az91d',
        name: 'AZ91D Magnesium',
        category: 'Exotic metal',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-cast-iron',
        name: 'Cast Iron',
        category: 'Steel',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-pvc-cr',
        name: 'Chemical-Resistant PVC',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steeld2',
        name: 'D2 Tool Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-delrin-gf20',
        name: 'Delrin (20% Glass-Filled)',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-delrin',
        name: 'Delrin 150',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-delrin-af',
        name: 'Delrin AF (13% PTFE-Filled)',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-galvannealed-steel',
        name: 'GALVANNEALED STEEL',
        category: 'Steel',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Material Certification'],
        inspections: [],
      },
      {
        id: 'cnc-g10',
        name: 'Garolite G-10',
        category: 'Composite Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-titanium-grade2',
        name: 'Grade 2 Titanium',
        category: 'Titanium',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-titanium-6al4v',
        name: 'Grade 5 Titanium',
        category: 'Titanium',
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-hdpe',
        name: 'HDPE',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-inconel-625',
        name: 'Inconel 625',
        category: null,
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-inconel-718',
        name: 'Inconel 718',
        category: null,
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-invar-36',
        name: 'Invar 36',
        category: 'Exotic metal',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-kovar',
        name: 'Kovar',
        category: 'Exotic metal',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-al-mic6',
        name: 'MIC-6 Aluminum',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-nitronic60',
        name: 'Nitronic 60',
        category: 'Exotic metal',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'passivation',
            name: 'Passivation',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-nylon-gf30',
        name: 'Nylon (30% Glass-Filled)',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-nylon',
        name: 'Nylon 6/6',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-o1-tool-steel',
        name: 'O1 Tool Steel',
        category: null,
        finishes: [
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-peek',
        name: 'PEEK',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-peek-gf30',
        name: 'PEEK (30% Glass-Filled)',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-polycarbonate',
        name: 'Polycarbonate',
        category: 'Plastic',
        finishes: [
          {
            id: 'vapor-polishing',
            name: 'Vapor Polishing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-pp',
        name: 'Polypropylene',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-pps',
        name: 'PPS (Polyphenylene Sulfide)',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ptfe',
        name: 'PTFE (Teflon)',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel1018',
        name: 'Steel 1018',
        category: 'Steel',
        finishes: [
          {
            id: 'black-oxide',
            name: 'Black Oxide',
          },
          {
            id: 'case-hardening',
            name: 'Case Hardening',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'hardening',
            name: 'Hardening',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'tempering',
            name: 'Tempering',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-torlon-4301',
        name: 'Torlon PAI (4301)',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-uhmw',
        name: 'UHMW',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-ultem1000',
        name: 'ULTEM 1000',
        category: 'Plastic',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-zinc-zamak3',
        name: 'Zamak 3 (Zinc)',
        category: 'Exotic metal',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cnc-steel1018-zg',
        name: 'Zinc-Galvanized Low-Carbon Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
    ],
    sheet_metal_fabrication: [
      {
        id: 'sm-steel-1008',
        name: '1008 Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-steel-1011',
        name: '1011 Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-cu-110',
        name: '110 Copper',
        category: 'Copper, Brass, Bronze',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-al-2024-t3',
        name: '2024-T3 Aluminum',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-al-3003-h14',
        name: '3003-H14 Aluminum',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-ss-301',
        name: '301 Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-ss-304',
        name: '304 Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-ss-316l',
        name: '316L Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-ss-430',
        name: '430 Stainless Steel',
        category: 'Stainless steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-al-5052-h32',
        name: '5052-H32 Aluminum',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-al-6061',
        name: '6061 Aluminum',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-al-7075',
        name: '7075 Aluminum',
        category: 'Aluminum',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-steel-a36',
        name: 'A36 Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-bronze-c220',
        name: 'C220 Bronze',
        category: 'Copper, Brass, Bronze',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-brass-c260',
        name: 'C260 Brass',
        category: 'Copper, Brass, Bronze',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-bronze-c510',
        name: 'C510 Bronze',
        category: 'Copper, Brass, Bronze',
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii',
            name: 'Anodize per Mil-PRF-8625 Type III',
          },
          {
            id: 'anodize-per-mil-prf-8625-type-iii-w-ptfe',
            name: 'Anodize per Mil-PRF-8625 Type III w/ PTFE',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-steel-galvanized',
        name: 'Galvanized Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-galvannealed-steel',
        name: 'GALVANNEALED STEEL',
        category: 'Steel',
        finishes: [],
        certificatesAllowed: true,
        certificates: ['Material Certification'],
        inspections: [],
      },
      {
        id: 'sm-titanium-grade2',
        name: 'Titanium Grade 2',
        category: 'Steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'sm-titanium-grade5',
        name: 'Titanium Grade 5',
        category: 'Steel',
        finishes: [
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
    ],
    injection_molding: [
      {
        id: 'resin-abs',
        name: 'ABS',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-delrin',
        name: 'Acetal/POM (Delrin)',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-asa',
        name: 'ASA',
        category: null,
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-hdpe',
        name: 'HDPE',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-hips',
        name: 'High Impact Polystyrene (HIPS)',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-ldpe',
        name: 'LDPE',
        category: null,
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-nylon-66',
        name: 'Nylon 6/6',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-nylon-gf',
        name: 'Nylon Glass-Filled',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-pbt',
        name: 'PBT',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-pbt-gf',
        name: 'PBT Glass-Filled',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-pc-abs',
        name: 'PC/ABS',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-peek',
        name: 'PEEK',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-peek-gf',
        name: 'PEEK Glass-Filled',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-ultem',
        name: 'PEI (Ultem)',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-pet',
        name: 'PET',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-acrylic',
        name: 'PMMA (Acrylic)',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-polycarbonate',
        name: 'Polycarbonate',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-pc-gf',
        name: 'Polycarbonate Glass-Filled',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-pe',
        name: 'Polyethylene',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-polypropylene',
        name: 'Polypropylene',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-ppa',
        name: 'PPA',
        category: null,
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-ppo',
        name: 'PPO',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-pps',
        name: 'PPS',
        category: null,
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-ppsu',
        name: 'PPSU',
        category: null,
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-pvc',
        name: 'PVC',
        category: null,
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-san-as',
        name: 'SAN (AS)',
        category: null,
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-silicone',
        name: 'Silicone',
        category: 'Rubber',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-tpe',
        name: 'Thermoplastic Elastomer (TPE)',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-tpu',
        name: 'TPU',
        category: 'Injection molding',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'resin-tpv',
        name: 'TPV',
        category: null,
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
    ],
    urethane_casting: [
      {
        id: 'rtv-pu8150',
        name: 'ABS-Like',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'silver',
                name: 'Silver',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'silver',
                name: 'Silver',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-abs-like-fr-v0',
        name: 'ABS-Like FR V0',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-abs-like-ht',
        name: 'ABS-Like HT',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-px521',
        name: 'Acrylic-Like',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-nylon-like',
        name: 'Nylon-Like',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-pu8400',
        name: 'Opaque Elastomer',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'pink',
                name: 'Pink',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-pc-like',
        name: 'PC-Like',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-pom-like',
        name: 'POM-Like',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-up5690',
        name: 'PP-Like',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'pu-8400',
        name: 'PU 8400',
        category: null,
        finishes: [],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'silicone',
        name: 'Silicone',
        category: null,
        finishes: [],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
      {
        id: 'rtv-t0387',
        name: 'Transparent Elastomer',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'green',
                name: 'Green',
              },
              {
                id: 'orange',
                name: 'Orange',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'purple',
                name: 'Purple',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: false,
        certificates: [],
        inspections: [],
      },
    ],
    '3d_printing': [
      {
        id: 'abs',
        name: 'ABS',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-abs-esd',
        name: 'ABS ESD',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-abs-m30i',
        name: 'ABS M30i',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'natural',
                name: 'Natural',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'abs-like',
        name: 'ABS-like',
        category: 'Urethane casting',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'gloss-black',
                name: 'Gloss-Black',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'gloss-black',
                name: 'Gloss-Black',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'gloss-black',
                name: 'Gloss-Black',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'gloss-black',
                name: 'Gloss-Black',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-accura-25',
        name: 'Accura 25',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-accura-60',
        name: 'Accura 60',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'pink',
                name: 'Pink',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'pink',
                name: 'Pink',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'pink',
                name: 'Pink',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'pink',
                name: 'Pink',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-accura-amx-rigid-black',
        name: 'Accura AMX Rigid Black',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-accura-clearvue',
        name: 'Accura ClearVue',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-accura-xtreme-grey',
        name: 'Accura Xtreme Gray',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-accura-xtreme-white-200',
        name: 'Accura Xtreme White 200',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-asa',
        name: 'ASA',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'dls-epu-40-41',
        name: 'EPU 40/41',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: [],
        inspections: [],
      },
      {
        id: 'dls-epx-82',
        name: 'EPX 82',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: [],
        inspections: [],
      },
      {
        id: 'dls-epx-86fr',
        name: 'EPX 86FR',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'dls-fpu-50',
        name: 'FPU 50',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'dls-loctite-3d-3843',
        name: 'Loctite 3D 3843',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'matte-black',
                name: 'Matte Black',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'matte-black',
                name: 'Matte Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'dls-loctite-3d-ind147',
        name: 'Loctite 3D IND147',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'dls-loctite-3d-ind405-clear',
        name: 'Loctite 3D IND405 Clear',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'dls-mpu-100',
        name: 'MPU 100',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-nylon-12',
        name: 'Nylon 12',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'nylon',
        name: 'Nylon 12',
        category: 'Selective Laser Sintering (SLS)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'dyeing',
            name: 'Dyeing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sls-nylon-12-fire-retardant',
        name: 'Nylon 12 Fire Retardant',
        category: 'Selective Laser Sintering (SLS)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'dyeing',
            name: 'Dyeing',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sls-nylon-gf',
        name: 'Nylon 12 Glass-Filled',
        category: 'Selective Laser Sintering (SLS)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'dyeing',
            name: 'Dyeing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-nylon-6',
        name: 'Nylon 6 CF',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'mjf-pa12',
        name: 'PA 12',
        category: 'Multi Jet Fusion (MJF)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'dyeing',
            name: 'Dyeing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'mjf-pa12-gb',
        name: 'PA 12 Glass Beads',
        category: 'Multi Jet Fusion (MJF)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'dyeing',
            name: 'Dyeing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-polycarbonate-abs',
        name: 'PC + ABS',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-pc-iso',
        name: 'PC-ISO',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-petg',
        name: 'PETG',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'neutral',
                name: 'Neutral',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-polycarbonate',
        name: 'Polycarbonate',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'dls-rpu-70',
        name: 'RPU 70',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: [],
      },
      {
        id: 'rubber-like',
        name: 'Rubber-Like',
        category: 'PolyJet',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'multi-color',
                name: 'Multi-Color',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'multi-color',
                name: 'Multi-Color',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'multi-color',
                name: 'Multi-Color',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'multi-color',
                name: 'Multi-Color',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-somos-evolve',
        name: 'Somos Evolve',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-somos-perform',
        name: 'Somos Perform',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-somos-waterclear',
        name: 'Somos Waterclear',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sla-somos-watershed',
        name: 'Somos Watershed',
        category: 'Stereolithography (SLA)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'sls-tpu-88a',
        name: 'TPU 88A',
        category: 'Selective Laser Sintering (SLS)',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'dyeing',
            name: 'Dyeing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'vapor-smoothing',
            name: 'Vapor Smoothing',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-ultem-1010',
        name: 'Ultem 1010',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'amber',
                name: 'Amber',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'amber',
                name: 'Amber',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'amber',
                name: 'Amber',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'amber',
                name: 'Amber',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'fdm-ultem-9085',
        name: 'Ultem 9085',
        category: 'Fused Deposition Modeling (FDM)',
        finishes: [
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'tan',
                name: 'Tan',
              },
            ],
          },
          {
            id: 'polishing',
            name: 'Polishing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'tan',
                name: 'Tan',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'tan',
                name: 'Tan',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'tan',
                name: 'Tan',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'dls-uma-90',
        name: 'UMA 90',
        category: 'Carbon DLS',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'cyan',
                name: 'Cyan',
              },
              {
                id: 'magenta',
                name: 'Magenta',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'cyan',
                name: 'Cyan',
              },
              {
                id: 'magenta',
                name: 'Magenta',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'cyan',
                name: 'Cyan',
              },
              {
                id: 'magenta',
                name: 'Magenta',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'cyan',
                name: 'Cyan',
              },
              {
                id: 'magenta',
                name: 'Magenta',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'blue',
                name: 'Blue',
              },
              {
                id: 'cyan',
                name: 'Cyan',
              },
              {
                id: 'magenta',
                name: 'Magenta',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'red',
                name: 'Red',
              },
              {
                id: 'white',
                name: 'White',
              },
              {
                id: 'yellow',
                name: 'Yellow',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'veroblack',
        name: 'VeroBlack',
        category: 'PolyJet',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'black',
                name: 'Black',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'transparent',
        name: 'VeroClear',
        category: 'PolyJet',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'clear',
                name: 'Clear',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
      {
        id: 'verowhite',
        name: 'VeroWhite',
        category: 'PolyJet',
        finishes: [
          {
            id: 'clear-coat',
            name: 'Clear Coat',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'priming',
            name: 'Priming',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'sanding',
            name: 'Sanding',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity'],
        inspections: ['Advanced Inspection Report', 'Dimensional Inspection Report'],
      },
    ],
    compression_molding: [
      {
        id: '718h',
        name: '718H',
        category: null,
        finishes: [],
        certificatesAllowed: true,
        certificates: [],
        inspections: [],
      },
      {
        id: 'cm-epdm-rubber',
        name: 'EPDM Rubber',
        category: 'Rubber',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'cm-silicone',
        name: 'Silicone',
        category: 'Rubber',
        finishes: [
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
          {
            id: 'painting',
            name: 'Painting',
            colors: [
              {
                id: 'as-per-rqmt',
                name: 'As per rqmt',
              },
              {
                id: 'black',
                name: 'Black',
              },
              {
                id: 'clear',
                name: 'Clear',
              },
              {
                id: 'gray',
                name: 'Gray',
              },
              {
                id: 'natural',
                name: 'Natural',
              },
              {
                id: 'pantone',
                name: 'Pantone',
              },
              {
                id: 'ral',
                name: 'RAL',
              },
              {
                id: 'white',
                name: 'White',
              },
            ],
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
    ],
    die_casting: [
      {
        id: 'a380',
        name: 'A380 Aluminum',
        category: null,
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'blasting',
            name: 'Blasting',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'e-coating-electro-coating',
            name: 'E-Coating (Electro Coating)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-polishing',
            name: 'Vibratory Polishing',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'a383',
        name: 'A383 Aluminum',
        category: null,
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'blasting',
            name: 'Blasting',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'e-coating-electro-coating',
            name: 'E-Coating (Electro Coating)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-polishing',
            name: 'Vibratory Polishing',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'a390-aluminum',
        name: 'A390 Aluminum',
        category: null,
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'adc10',
        name: 'ADC10 Aluminum',
        category: null,
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'blasting',
            name: 'Blasting',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'e-coating-electro-coating',
            name: 'E-Coating (Electro Coating)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-polishing',
            name: 'Vibratory Polishing',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'adc12',
        name: 'ADC12 Aluminum',
        category: null,
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'blasting',
            name: 'Blasting',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'e-coating-electro-coating',
            name: 'E-Coating (Electro Coating)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-polishing',
            name: 'Vibratory Polishing',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'az91d',
        name: 'AZ91D Magnesium',
        category: null,
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'blasting',
            name: 'Blasting',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-polishing',
            name: 'Vibratory Polishing',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'zamak-3',
        name: 'Zamak 3 Zinc Alloy',
        category: null,
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'blasting',
            name: 'Blasting',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-polishing',
            name: 'Vibratory Polishing',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
      {
        id: 'zamak-5',
        name: 'Zamak 5 Zinc Alloy',
        category: null,
        finishes: [
          {
            id: 'anodize-per-mil-prf-8625-type-ii',
            name: 'Anodize per Mil-PRF-8625 Type II',
          },
          {
            id: 'blasting',
            name: 'Blasting',
          },
          {
            id: 'chem-film-alodinetm',
            name: 'Chem Film (AlodineTM)',
          },
          {
            id: 'enp-electroless-nickel-plating',
            name: 'ENP (Electroless Nickel Plating)',
          },
          {
            id: 'laser-engraving',
            name: 'Laser Engraving',
          },
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'nickel-plating',
            name: 'Nickel Plating',
          },
          {
            id: 'pad-printing',
            name: 'Pad Printing',
          },
          {
            id: 'painting',
            name: 'Painting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'vibratory-polishing',
            name: 'Vibratory Polishing',
          },
          {
            id: 'vibratory-tumble',
            name: 'Vibratory Tumble',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
    ],
    tube_bending: [
      {
        id: 'tube-bending-a36-steel',
        name: 'A36 Steel',
        category: 'Steel',
        finishes: [
          {
            id: 'media-blasting',
            name: 'Media Blasting',
          },
          {
            id: 'powder-coating',
            name: 'Powder Coating',
          },
          {
            id: 'zinc-plating',
            name: 'Zinc Plating',
          },
        ],
        certificatesAllowed: true,
        certificates: ['Certificate of Conformity', 'Material Certification'],
        inspections: ['Advanced Inspection Report'],
      },
    ],
  }
